<template>
  <div>
    <div class="custom-search d-flex justify-content-end">
      <div v-if="$acl.canCreatePermission(role)" class="float-left mr-auto">
        <b-button
            variant="primary"
            @click="newEntity"
        >
          <span class="text-nowrap">Hozzáadás</span>
        </b-button>
      </div>
      <b-form-group>
        <div class="d-flex align-items-center">
          <label class="mr-1">Keresés</label>
          <b-form-input
              v-model="searchTerm"
              placeholder="Keresés..."
              type="text"
              class="d-inline-block"
              @input="globalSearch"
          />
        </div>
      </b-form-group>
      <b-form-group class="ml-2">
            <div class="d-flex align-items-center">
                <label class="text-nowrap mr-1">Dátum szűrés</label>
                <flat-pickr
                        v-model="columnFilters.date_range"
                        class="form-control"
                        :config="flatPickRConfig"
                        @input="onColumnFilter"
                />
            </div>
      </b-form-group>
    </div>
    <b-overlay
        :show="loading"
        spinner-variant="primary"
        spinner-type="grow"
        rounded="sm"
    >
      <!-- table -->
      <vue-good-table
          mode="remote"
          :columns="columns"
          :rows="rows"
          :rtl="direction" íí
          :pagination-options="{
          enabled: true,
          perPage: pageLength
        }"
          :sort-options="{
          enabled: true,
          initialSortBy: sortBy
        }"
          :row-style-class="rowStyleClassFn"
          @on-row-click="onRowClick"
          @on-sort-change="onSortChange"
          @on-column-filter="onColumnFilter"
      >
        <div slot="emptystate">
          <div class="vgt-center-align vgt-text-disabled">
            {{ loading ? 'Adatok betöltése ...' : $t('table.list.empty') }}
          </div>
        </div>
        <template
            slot="table-row"
            slot-scope="props"
        >

          <!-- Column: Name -->
          <span
              v-if="props.column.field === 'fisherman_nationality'"
              class="text-nowrap"
          >
                <span class="text-nowrap" v-if="props.row.fisherman_nationality && props.row.fisherman_nationality !== 'UNKNOWN'">{{
                    nationalities.getName(props.row.fisherman_nationality, "hu")
                  }} ({{ props.row.fisherman_nationality }})</span>
                <span class="text-nowrap" v-else>Ismeretlen</span>
              </span>

          <span
              v-else-if="props.column.field === 'weight'"
              class="text-nowrap"
          >
                <span class="text-nowrap">{{ formatFloatToComma(props.row.weight) }} kg</span>
              </span>

          <span
              v-else-if="props.column.field === 'images'"
          >
              <div style="display: flex; gap: 3px" v-if="props.row.images.length > 0">

                <div v-for="image in props.row.images" :key="image.id">
                  <img :src="image.publicUrl" loading="lazy" style="max-width: 70px" :alt="image.alt"
                       v-b-modal.list-record-catch-gallery-modal
                       @click.stop="showGallery(props.row.id)"/>
                  <div style="display: flex; align-items: center; justify-content: stretch">
                    <div :class="{'bg-success': image.visible, 'bg-danger': !image.visible}" class="d-flex align-items-center justify-content-center" style="flex: 1; min-height: 20px">
                      <feather-icon
                          v-if="image.visible"
                          v-b-tooltip.hover
                          title="Látható"
                          class="text-white"
                          icon="CheckIcon"
                          size="14"
                      />
                      <feather-icon
                          v-else
                          v-b-tooltip.hover
                          title="Nem látható"
                          class="text-white"
                          icon="XIcon"
                          size="14"
                      />
                    </div>
                    <div v-if="image.primary" class="d-flex align-items-center justify-content-center bg-warning" style="flex: 1; min-height: 20px">
                      <feather-icon
                          v-b-tooltip.hover
                          title="Elsődleges fotó"
                          class="text-white"
                          icon="StarIcon"
                          size="14"
                      />
                    </div>
                    <div v-if="image.pr" class="d-flex align-items-center justify-content-center bg-info" style="flex: 1; min-height: 20px">
                      <feather-icon
                          v-b-tooltip.hover
                          title="PR fotó"
                          class="text-white"
                          icon="CameraIcon"
                          size="14"
                      />
                    </div>
                  </div>
                </div>

              </div>
          </span>

          <span
              v-else-if="props.column.field === 'length'"
              class="text-nowrap"
          >
                <span class="text-nowrap">{{ props.row.length ? props.row.length + ' cm' : '-' }}</span>
              </span>

          <span
              v-else-if="props.column.field === 'perimeter'"
              class="text-nowrap"
          >
                <span class="text-nowrap">{{ props.row.perimeter ? props.row.perimeter + ' cm' : '-' }}</span>
              </span>

          <span
              v-else-if="props.column.field === 'catch_time'"
              class="text-nowrap"
          >
                <span class="text-nowrap">{{ props.row.catch_time }}</span>
              </span>

          <span
              v-else-if="props.column.field === 'released'"
              class="text-nowrap"
          >
                <span class="text-nowrap">{{ props.row.released ? 'Igen' : 'Nem' }}</span>
              </span>

          <span
              v-else-if="props.column.field === 'visible'"
              class="text-nowrap"
          >
                <span class="text-nowrap">{{ props.row.visible ? 'Igen' : 'Nem' }}</span>
              </span>

          <!-- Column: Action -->
          <span v-else-if="props.column.field === 'action'">
                <span v-if="props.row.images.length > 0">
                  <b-button
                      v-b-tooltip.hover
                      title="Galéria megtekintése"
                      size="sm"
                      variant="info"
                      v-b-modal.list-record-catch-gallery-modal
                      @click.stop="showGallery(props.row.id)"
                  >
                    <feather-icon
                        icon="ImageIcon"
                    />
                  </b-button>
                </span>
                <span v-if="$acl.canEditPermission(role)">
                  <b-button
                      v-b-tooltip.hover
                      title="Szerkesztés"
                      size="sm"
                      variant="primary"
                      class="ml-1"
                      @click.stop="editRecordCatch(props.row.id)"
                  >
                    <feather-icon
                        icon="EditIcon"
                    />
                  </b-button>
                </span>
                <span v-if="$acl.canDeletePermission(role)">
                  <b-button
                      v-b-tooltip.hover
                      title="Törlés"
                      size="sm"
                      variant="danger"
                      class="ml-1"
                      @click.stop="deleteEntity(props.row.id, $event)"
                  >
                    <feather-icon
                        icon="XCircleIcon"
                    />
                  </b-button>
                </span>
              </span>

          <!-- Column: Common -->
          <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
        </template>

        <!-- pagination -->
        <template
            slot="pagination-bottom"
            slot-scope="props"
        >
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">
              {{ ((page - 1) * pageLength) + 1 }} +
            </span>
              <b-form-select
                  v-model="pageLength"
                  :options="pages"
                  class="mx-1"
                  @change="handlePageChange"
              />
              <span class="text-nowrap "> a {{ dataFromServer.total }} találatból </span>
            </div>
            <div>
              <b-pagination
                  :value="page"
                  :total-rows="dataFromServer.total"
                  :per-page="dataFromServer.per_page"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @change="handleChangePage"
              >
                <template #prev-text>
                  <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                  />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>
    </b-overlay>
    <gallery :entity-id="showGalleryId"></gallery>
    <span v-html="css"></span>
  </div>
</template>

<script>
import {
  BAvatar, BBadge, BPagination, BFormGroup, BFormInput, BFormSelect, BDropdown, BDropdownItem, BButton, BOverlay, VBTooltip,
} from 'bootstrap-vue'
import {VueGoodTable} from 'vue-good-table'
import store from '@/store/index'
import gallery from "@/views/pages/_components/_modal/record-catch/gallery.vue";
import { formatFloatToComma } from "@/libs/formatter/numberFormatter"
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css';
import {Hungarian} from 'flatpickr/dist/l10n/hu.js'

export default {
  components: {
    gallery,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    BButton,
    BOverlay,
    VBTooltip,
    flatPickr,
  },
  directives: {
      'b-tooltip': VBTooltip,
  },
  data() {
    return {
      css: '',
      role: 'record_catch',
      loading: false,
      pageLength: 10,
      dir: false,
      pages: ['10', '20', '50'],
      columns: [
        {
          label: this.$t('form.id'),
          field: 'id',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Az. szerinti szűrés',
          },
          width: '5%',
        },
        {
          label: 'Képek',
          field: 'images',
          sortable: false,
          filterOptions: {
            enabled: false,
          },
          width: '1%'
        },
        {
          label: 'Halfaj',
          field: 'fish_type_name',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Halfaj szerinti szűrés',
          },
          width: '5%',
        },
        {
          label: 'Horgász',
          field: 'fisherman_name',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Horgász szerinti szűrés',
          },
          width: '5%',
        },
        {
          label: 'Tömeg',
          field: 'weight',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Tömeg szerinti szűrés',
          },
          width: '5%',
        },
        {
          label: 'Hossz',
          field: 'length',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Hossz szerinti szűrés',
          },
          width: '5%',
        },
        {
          label: 'Kerület',
          field: 'perimeter',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Kerület szerinti szűrés',
          },
          width: '5%',
        },
        {
          label: 'Fogás időpontja',
          field: 'catch_time',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Fogás időpontja szerinti szűrés',
          },
          width: '5%',
        },
        {
          label: 'Látható',
          field: 'visible',
          sortable: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Láthatóság szerinti szűrés',
            filterDropdownItems: [
              {value: true, text: 'Igen'},
              {value: false, text: 'Nem'}
            ]
          },
          width: '5%',
        },
      ],
      showGalleryId: '',
      rows: [],
      page: 1,
      dataFromServer: [],
      filterData: [],
      searchTerm: '',
      sortBy: {
          field: 'id',
          type: 'desc',
      },
      columnFilters: {
          date_range: '',
      },
      nationalities: '',
      flatPickRConfig: {
          enableTime: true,
          dateFormat: 'Y-m-d H:i',
          locale: Hungarian,
          allowInput: true,
          mode: 'range'
      },
      formatFloatToComma,
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
  },
  created() {
    let storedPageLength = localStorage.getItem('pageLength')
    if (storedPageLength) {
      this.pageLength = storedPageLength
    }

      let storedSearchTerm = localStorage.getItem('recordCatchOverviewSearchTerm')
      if (storedSearchTerm) {
          this.searchTerm = storedSearchTerm
      }

      let storedColumnFilters = localStorage.getItem('recordCatchOverviewColumnFilters')
      if (storedColumnFilters) {
          this.columnFilters = JSON.parse(storedColumnFilters)
          Object.keys(this.columnFilters).forEach(key => {
              const value = this.columnFilters[key]
              const foundIndex = this.columns.findIndex(col => col.field === key)

              if (foundIndex !== -1) {
                  this.$set(this.columns[foundIndex].filterOptions, 'filterValue', value)
              }
          })
      }

    let nationalities = require("i18n-iso-countries")
    nationalities.registerLocale(require("i18n-iso-countries/langs/hu.json"));
    this.nationalities = nationalities

    if (this.$acl.canEditPermission(this.role) || this.$acl.canDeletePermission(this.role)) {
      const actions = [
        {
          label: this.$t('table.header.actions'),
          field: 'action',
          sortable: false,
          width: '15rem',
        },
      ]

      this.columns = [...actions, ...this.columns]
    }

    this.$store.dispatch('fetchFishTypes', {limit: 100000}).then(response => {
      this.css = '<style>';
      Object.keys(response.data.entities.data).forEach(key => {
        this.css += '.color-' + response.data.entities.data[key].color.replace('#', '') + '{ background-color: ' + response.data.entities.data[key].color + ' }';
        this.css += 'table.vgt-table tr.color-' + response.data.entities.data[key].color.replace('#', '') + ':hover{ background-color: ' + response.data.entities.data[key].color + ' !important }';
      })
    }).finally(() => {
      this.css += '</style>'
    })

    this.initEntities()
  },
  methods: {
    initEntities() {
      this.loading = true
      const searchParams = {
        page: parseInt(this.page),
        limit: parseInt(this.pageLength),
        query: this.searchTerm,
        columnFilters: this.columnFilters,
        sort: this.sortBy,
      }

      this.$store.dispatch('fetchRecordCatches', searchParams).then(response => {
        this.rows = response.data.entities.data
        this.dataFromServer = response.data.entities
        this.page = this.dataFromServer.current_page
        this.pageLength = parseInt(this.dataFromServer.per_page)
      }).finally(() => {
        this.loading = false
      })
    },
    onRowClick(params) {
      if (this.$acl.canEditPermission(this.role))
        this.$router.push({name: 'modify-record-catch', params: {id: params.row.id}})
    },
    handleChangePage(page) {
      this.page = page
      this.initEntities()
    },
    handlePageChange(active) {
      this.pageLength = active
      localStorage.setItem('pageLength', active)
      this.page = 1
      this.handleChangePage()
    },
    newEntity() {
      this.$router.push({name: 'add-record-catch'})
    },
    deleteEntity(id, event) {
      this.$bvModal
          .msgBoxConfirm('Biztosan törölni szeretnéd?', {
            title: 'Jóváhagyás',
            size: 'sm',
            okVariant: 'primary',
            okTitle: 'Igen',
            cancelTitle: 'Nem',
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          })
          .then(value => {
            if (value) {
              // axios
              this.$store.dispatch('deleteRecordCatch', id).then(() => {
                this.initEntities(1)
                this.$helpers.showSuccessToast(`A rekordot töröltük. (ID: ${id})`)
              })
            }
          })
    },
    globalSearch(params) {
      this.page = 1
      this.query = params.searchTerm
      localStorage.setItem('recordCatchOverviewSearchTerm', this.searchTerm)
      this.initEntities()
    },
    onSortChange(params) {
      this.page = 1
      this.sortBy = params[0]
      this.initEntities()
    },
      onColumnFilter(params) {
          this.page = 1
          this.columnFilters = {
              ...this.columnFilters,
              ...params.columnFilters
          };
          localStorage.setItem('recordCatchOverviewColumnFilters', JSON.stringify(this.columnFilters))
          this.initEntities()
      },
    showGallery(id) {
      this.showGalleryId = id
    },
    rowStyleClassFn(row) {
      return 'color-' + row.fish_type_color.replace('#', '')
    },
    editRecordCatch(id) {
      let routeData = this.$router.resolve({name: 'modify-record-catch', params: {id: id}})
      window.open(routeData.href, '_blank')
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@media (max-width: 576px) {
  .custom-search {
    flex-direction: column;
  }

  .custom-search div {
    display: flex;
    flex-direction: column;
    margin: 0 !important;
    float: none !important;
  }

  .custom-search div button, .custom-serach div input {
    width: 100%;
    margin-bottom: 15px;
  }

  .form-group {
    margin-left: 0px !important;
  }
}
</style>
